import axios from "../../../utils/axios";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import {
    IProviderSessionStatsFilters,
    ISendStatementOfAccount,
    ISessionActivityFilters,
    IStatementOfAccountFilters,
    IExportStatementOfAccountFilters,
    ITerminatedClientsFilters,
    IConfirmationReportFilters,
    INoteAuditReportFilters,
    IAgingReportFilters,
    IConsentReportFilters,
    ICreateConsentRequest,
    IUpdateConsentRequest,
    IConsentsNeedingRenewalResponse,
    IRenewConsentRequest,
    ICollateralConsentReportFilters,
    IUpdateCollateralConsentStatusRequest,
    ICreateProviderAccommodationPayload,
    IProviderAccommodationFilters,
    IProviderAccommodationPayload,
} from "../types/reports.types";

// Endpoint to get the statement of account for clients
export async function getStatementOfAccount(
    filters: IStatementOfAccountFilters
) {
    const response = await axios.get(`report/client/account-statement`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

// Endpoint to send statement of account via email
export async function sendStatementOfAccount(
    payload: ISendStatementOfAccount,
    filters: IStatementOfAccountFilters
) {
    const response = await axios.post(
        `report/client/account-statement`,
        payload,
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
        }
    );
    return response.data;
}

// Endpoint to download the statement of account pdf
export async function downloadStatementOfAccount(
    pdfType: string,
    exportJobId: string
) {
    await axios
        .get(`/pdf/export/${pdfType}/${exportJobId}`, {
            responseType: "blob",
        })
        .then((res) => {
            const href = window.URL.createObjectURL(res.data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        });
}

// Endpoint to export the statement of account as a spreadsheet
export async function exportStatementOfAccount(
    filters: IExportStatementOfAccountFilters
) {
    const response = await axios.get(
        "/report/client/account-statement/export",
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
            responseType: "blob",
        }
    );

    return response.data;
}

// Endpoint to get provider session activity report
export async function fetchProviderSessionActivityReport(
    filters: ISessionActivityFilters
) {
    const response = await axios.get("/report/provider/session-activity", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function fetchProviderSessionStats(
    providerId: string,
    filters: IProviderSessionStatsFilters
) {
    const response = await axios.get(
        `/report/provider/${providerId}/session-activity`,
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
        }
    );

    return response.data;
}

// Endpoint to generate spreadsheet for provider session activity report
export async function exportProviderSessionActivityReport(
    filters: ISessionActivityFilters
) {
    const response = await axios.get(
        "/report/provider/session-activity/export",
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
            responseType: "blob",
        }
    );

    return response.data;
}

// Endpoint to get and filter terminated clients
export async function getTerminatedClients(filters: ITerminatedClientsFilters) {
    const response = await axios.get("/report/terminated-clients", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });

    return response.data;
}

// Endpoint to export terminated clients
export async function exportTerminatedClients(
    filters: ITerminatedClientsFilters
) {
    const response = await axios.get("/report/terminated-clients/export", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
        responseType: "blob",
    });

    return response.data;
}

// Endpoint to get confirmation reports
export async function getConfirmationReports(
    filters: IConfirmationReportFilters
) {
    const response = await axios.get("/sessions/confirmation-reports", {
        params: handleRemoveEmptyParamFromQueryParams({
            ...filters,
            endpoint_version: "v2",
        }),
    });
    return response.data;
}

export async function getNoteAuditReport(filters: INoteAuditReportFilters) {
    const response = await axios.get("/report/note-audit", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function getProviderNoteAuditReport(
    providerId: string,
    filter: INoteAuditReportFilters
) {
    const response = await axios.get(`/report/note-audit/${providerId}`, {
        params: handleRemoveEmptyParamFromQueryParams(filter),
    });
    return response.data;
}

export async function getAgingReport(filters: IAgingReportFilters) {
    const response = await axios.get("/report/aging-report", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function exportAgingReport(filters: IAgingReportFilters) {
    const response = await axios.get("/report/aging-report/export", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
        responseType: "blob",
    });
    return response.data;
}

// Consent Reports API functions
export async function getConsentReports(filters: IConsentReportFilters) {
    const response = await axios.get("/consent/reports", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function getCollateralConsentReports(
    filters: ICollateralConsentReportFilters
) {
    const response = await axios.get("/collateral-consents", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function createConsent(data: ICreateConsentRequest) {
    const { client_id: clientId, ...requestBody } = data;
    const response = await axios.post(
        `/client/${clientId}/consent`,
        requestBody
    );
    return response.data;
}

export async function updateConsent(
    consentId: string,
    data: IUpdateConsentRequest
) {
    const response = await axios.patch(`/client/consent/${consentId}`, data);
    return response.data;
}

export async function updateCollateralConsentStatus(
    collateralConsentId: string,
    data: IUpdateCollateralConsentStatusRequest
) {
    const response = await axios.patch(
        `/client/collateral-consents/${collateralConsentId}/status`,
        data
    );
    return response.data;
}

export async function uploadConsentDocuments(
    consentId: string,
    formData: FormData
) {
    const response = await axios.post(
        `/client/consent/${consentId}/documents`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );
    return response.data;
}

export async function getClientActiveSessions(clientId: string) {
    const response = await axios.get(`/client/${clientId}/active-sessions`);
    return response.data;
}

export async function exportConsentReports(filters: IConsentReportFilters) {
    const response = await axios.get("/consent/reports/export", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
        responseType: "blob",
    });
    return response.data;
}

export async function getConsentsNeedingRenewal(filters: {
    limit?: number;
    offset?: number;
}): Promise<IConsentsNeedingRenewalResponse> {
    const response = await axios.get("/consent/reports/needing-renewal", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function renewConsent(
    consentId: string,
    data: IRenewConsentRequest
) {
    const response = await axios.post(`/consent/${consentId}/renew`, data);
    return response.data;
}

export async function getExpiringConsents(filters: IConsentReportFilters) {
    const response = await axios.get("/consent/reports/expiring-consents", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function exportExpiringConsents(filters: IConsentReportFilters) {
    const response = await axios.get(
        "/consent/reports/expiring-consents/export",
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
            responseType: "blob",
        }
    );
    return response.data;
}

export async function fetchProviderAccommodations(
    filters: IProviderAccommodationFilters
) {
    const response = await axios.get(`/provider-accommodations`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function createProviderAccommodation(
    payload: ICreateProviderAccommodationPayload
) {
    const response = await axios.post(`/provider-accommodations`, payload);
    return response.data;
}

export async function updateProviderAccommodation(
    accommodationId: string,
    payload: IProviderAccommodationPayload
) {
    const response = await axios.patch(
        `/provider-accommodations/${accommodationId}`,
        payload
    );
    return response.data;
}

export async function deleteProviderAccommodation(
    accommodationId: string,
    reason: string
) {
    const response = await axios.delete(
        `/provider-accommodations/${accommodationId}`,
        { data: { reason } }
    );
    return response.data;
}
